import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { caseNotesService } from '../services/api';

const CaseNotes = ({ case_ }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [cerca, setCerca] = useState('');
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    is_important: false
  });

  // Les notes dels companys arriben en directe: abans no se n'assabentava
  // ningú fins a la següent recàrrega.
  useEffect(() => {
    const aplicaCanvi = (event) => {
      const { entity, action, data, user } = event.detail || {};
      if (entity !== 'note') return;
      if (!user?.user_id || !currentUser?.id || user.user_id === currentUser.id) return;
      setNotes(prev => {
        if (action === 'deleted') return prev.filter(n => n.id !== data.id);
        const i = prev.findIndex(n => n.id === data.id);
        if (i === -1) return [data, ...prev];
        const copia = [...prev];
        copia[i] = { ...copia[i], ...data };
        return copia;
      });
    };
    window.addEventListener('collaboration-entity-change', aplicaCanvi);
    return () => window.removeEventListener('collaboration-entity-change', aplicaCanvi);
  }, [currentUser?.id]);

  useEffect(() => {
    if (case_?.id) {
      fetchNotes();
    }
  }, [case_]);

  const fetchNotes = async () => {
    try {
      const data = await caseNotesService.getCaseNotes(case_.id);
      setNotes(data);
    } catch (error) {
      toast.error(t('notes.fetchError'));
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error(t('notes.fillBothFields'));
      return;
    }

    try {
      await caseNotesService.createNote({
        ...newNote,
        case_id: case_.id
      });
      
      setShowCreateModal(false);
      setNewNote({ title: '', content: '', is_important: false });
      fetchNotes();
    } catch (error) {
      toast.error(t('notes.createError'));
      console.error('Error creating note:', error);
    }
  };

  const handleEditNote = async (e) => {
    e.preventDefault();
    
    if (!editingNote.title.trim() || !editingNote.content.trim()) {
      toast.error(t('notes.fillBothFields'));
      return;
    }

    try {
      await caseNotesService.updateNote(editingNote.id, {
        title: editingNote.title,
        content: editingNote.content,
        is_important: editingNote.is_important
      });
      
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error(t('notes.updateError'));
      console.error('Error updating note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(t('notes.confirmDelete'))) {
      return;
    }

    try {
      await caseNotesService.deleteNote(noteId);
      fetchNotes();
    } catch (error) {
      toast.error(t('notes.deleteError'));
      console.error('Error deleting note:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const canEditNote = (note) => {
    // El rol arriba en minúscules a tot arreu ('administrator'); aquí es
    // comparava amb 'ADMINISTRATOR' i la condició no s'ha complert mai, o
    // sigui que un administrador no podia tocar cap nota que no fos seva.
    return currentUser?.role === 'administrator' || note.author_id === currentUser?.id;
  };

  // Marcar una nota com a important era entrar a l'edició, canviar una casella
  // i desar. En una sessió és el gest que més es fa i el que costava més.
  const canviaImportancia = async (note) => {
    const nova = !note.is_important;
    setNotes(prev => prev.map(n => (n.id === note.id ? { ...n, is_important: nova } : n)));
    try {
      await caseNotesService.updateNote(note.id, {
        title: note.title,
        content: note.content,
        is_important: nova
      });
    } catch (error) {
      console.error('Error updating note:', error);
      setNotes(prev => prev.map(n => (n.id === note.id ? { ...n, is_important: !nova } : n)));
      toast.error(t('notes.updateError'));
    }
  };

  // Cerca i ordre.
  //
  // Les notes s'acumulen i el que es busca no és «la tercera de la llista»
  // sinó «aquella on algú deia una cosa del gas». D'aquí la cerca, que mira
  // títol, contingut i autor alhora.
  //
  // I les importants van sempre a dalt. Abans quedaven barrejades per data i
  // el que les distingia era el color del rectangle: si en tenies quinze,
  // la important podia ser la darrera de tot.
  const cercaNeta = cerca.trim().toLowerCase();
  const visibles = notes
    .filter(n => !cercaNeta || [n.title, n.content, n.author_name]
      .some(camp => (camp || '').toLowerCase().includes(cercaNeta)))
    .sort((a, b) => {
      if (!!a.is_important !== !!b.is_important) return a.is_important ? -1 : 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  if (loading) {
    return (
      <div className="p-6 text-center text-neutral-500">
        {t('notes.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Capçalera */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">{t('notes.title')}</h3>
          <p className="mt-1 text-sm text-neutral-600">{t('notes.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex flex-shrink-0 items-center rounded-md border border-transparent bg-accent-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-700"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('notes.addNote')}
        </button>
      </div>

      {/* Cerca. Només surt quan hi ha prou notes perquè valgui la pena: amb
          dues, una caixa de cerca és soroll. */}
      {notes.length > 3 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[16rem] flex-1">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              placeholder={t('notes.searchPlaceholder')}
              className="w-full rounded-md border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm text-neutral-900 placeholder-neutral-400 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
            />
          </div>
          <span className="text-xs text-neutral-500">
            {cercaNeta
              ? t('notes.countFiltered', { shown: visibles.length, total: notes.length })
              : t('notes.count', { total: notes.length })}
          </span>
        </div>
      )}

      {/* Llista */}
      <div className="space-y-3">
        {notes.length > 0 && visibles.length === 0 ? (
          /* Hi ha notes, però cap que quadri amb la cerca. Sense aquest cas la
             llista es quedava en blanc i semblava que s'haguessin esborrat. */
          <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-10 text-center text-sm text-neutral-500">
            {t('notes.noMatches', { query: cerca.trim() })}
          </p>
        ) : notes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-neutral-900 mb-2">{t('notes.noNotesYet')}</h4>
            <p className="text-neutral-600 mb-4">
              {t('notes.noNotesMessage')}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="text-accent-600 hover:text-accent-700 font-medium"
            >
              {t('notes.addFirstNote')}
            </button>
          </div>
        ) : (
          visibles.map((note) => (
            <div
              key={note.id}
              /* Abans la nota important era un rectangle groc sencer amb un
                 filet de 2 px. Amb tres de seguides la pantalla es tornava
                 groga i el que hi deia no es llegia més. Ara el que la marca
                 és una barra estreta a l'esquerra, que és igual de visible
                 d'una ullada i no es menja el text. */
              className={`rounded-lg border bg-white p-4 transition-colors ${
                note.is_important
                  ? 'border-neutral-200 border-l-[3px] border-l-warning-500'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-neutral-900">{note.title}</h4>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    {note.author_name} · {formatDate(note.created_at)}
                    {note.updated_at && note.updated_at !== note.created_at && (
                      <span> · {t('notes.edited')} {formatDate(note.updated_at)}</span>
                    )}
                  </p>
                </div>

                <div className="flex flex-shrink-0 items-center gap-1">
                  {/* Marcar-la com a important és un clic. Qui no la pot editar
                      la veu marcada igualment, però el botó no hi és. */}
                  {canEditNote(note) ? (
                    <button
                      onClick={() => canviaImportancia(note)}
                      aria-pressed={!!note.is_important}
                      title={note.is_important ? t('notes.unmarkImportant') : t('notes.markImportant')}
                      className={`rounded p-1.5 transition-colors ${
                        note.is_important
                          ? 'text-warning-600 hover:bg-neutral-100'
                          : 'text-neutral-300 hover:bg-neutral-100 hover:text-neutral-500'
                      }`}
                    >
                      <svg className="h-4 w-4" fill={note.is_important ? 'currentColor' : 'none'}
                           stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                  ) : note.is_important && (
                    <span className="p-1.5 text-warning-600" title={t('notes.important')}>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </span>
                  )}

                  {canEditNote(note) && (
                    <>
                      <button
                        onClick={() => setEditingNote(note)}
                        title={t('notes.editNote')}
                        className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        title={t('common.delete')}
                        className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-error-dark"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>

              <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">
                {note.content}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Create Note Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-medium text-neutral-900">{t('notes.addNewNote')}</h3>
            </div>
            
            <form onSubmit={handleCreateNote} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('notes.titleLabel')}
                </label>
                <input
                  type="text"
                  value={newNote.title}
                  onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                  placeholder={t('notes.titlePlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('notes.contentLabel')}
                </label>
                <textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                  placeholder={t('notes.contentPlaceholder')}
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="important"
                  checked={newNote.is_important}
                  onChange={(e) => setNewNote({ ...newNote, is_important: e.target.checked })}
                  className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
                />
                <label htmlFor="important" className="ml-2 text-sm text-neutral-700">
                  {t('notes.markImportant')}
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 transition-colors"
                >
                  {t('notes.addNote')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Note Modal */}
      {editingNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-medium text-neutral-900">{t('notes.editNote')}</h3>
            </div>
            
            <form onSubmit={handleEditNote} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('notes.titleLabel')}
                </label>
                <input
                  type="text"
                  value={editingNote.title}
                  onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                  placeholder={t('notes.titlePlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('notes.contentLabel')}
                </label>
                <textarea
                  value={editingNote.content}
                  onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                  placeholder={t('notes.contentPlaceholder')}
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-important"
                  checked={editingNote.is_important}
                  onChange={(e) => setEditingNote({ ...editingNote, is_important: e.target.checked })}
                  className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
                />
                <label htmlFor="edit-important" className="ml-2 text-sm text-neutral-700">
                  {t('notes.markImportant')}
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingNote(null)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 transition-colors"
                >
                  {t('notes.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseNotes;